
const initialState = () => ({
    previews: []
    // Each preview object structure:
    // {
    //     id: unique id for this preview
    //     attachment: the attachment object
    //     attachmentKey: unique key for the attachment
    //     timelineId: the timeline/email ID this attachment belongs to
    //     openIn: 'inline' or 'sidepanel'
    //     loadedData: { blob, url, file } - cached data to prevent reloading
    // }
});

export const state = initialState();

export const getters = {
    // Get all previews for a specific timeline/email
    getTimelinePreviews: state => (timelineId, openIn = null) => {
        const previews = state.previews.filter(p => p.timelineId === timelineId);
        if (openIn) {
            return previews.filter(p => p.openIn === openIn);
        }
        return previews;
    },

    // Get a specific preview by ID
    getPreviewById: state => (id) => {
        return state.previews.find(p => p.id === id);
    },

    // Get the openIn state for a specific preview
    getPreviewOpenIn: state => (id) => {
        const preview = state.previews.find(p => p.id === id);
        return preview?.openIn || null;
    },

    // Check if an attachment is already being previewed
    isAttachmentPreviewed: state => (timelineId, attachmentId) => {
        return state.previews.some(p =>
            p.timelineId === timelineId && p.attachment.id === attachmentId
        );
    }
};

export const actions = {
    // Open a new preview
    openPreview({ commit, state }, { attachment, timelineId, openIn = 'inline' }) {
        // Check if this exact attachment is already being previewed in the same location
        const existing = state.previews.find(p =>
            p.id === attachment.id &&
            p.timelineId === timelineId &&
            p.openIn === openIn
        );

        if (existing) {
            return existing.id;
        }
        // Check if it exists in a different location (inline vs sidepanel)
        const existingDifferentLocation = state.previews.find(p =>
            p.id === attachment.id &&
            p.timelineId === timelineId &&
            p.openIn !== openIn
        );
        if (existingDifferentLocation) {
            // Move it to the new location, preserving data
            commit('SET_OPEN_IN', { id: existingDifferentLocation.id, openIn });
            return existingDifferentLocation.id;
        }
        // Create new preview
        const preview = {
            id: attachment.id,
            timelineId: timelineId,
            attachment,
            openIn,
            loadedData: null
        };
        commit('ADD_PREVIEW', preview);
        return preview.id;
    },

    // Store loaded data for a preview
    setPreviewData({ commit }, { id, loadedData }) {
        commit('SET_PREVIEW_DATA', { id, loadedData });
    },

    // Close a preview
    closePreview({ commit }, id) {
        commit('REMOVE_PREVIEW', id);
    },

    // Toggle between inline and sidepanel
    toggleOpenIn({ commit }, id) {
        commit('TOGGLE_OPEN_IN', id);
    },

    // Set specific openIn state
    setOpenIn({ commit }, { id, openIn }) {
        commit('SET_OPEN_IN', { id, openIn });
    },

    // Close all previews for a timeline
    closeTimelinePreviews({ commit }, timelineId) {
        commit('REMOVE_TIMELINE_PREVIEWS', timelineId);
    },

    // Toggle all previews in a timeline from one openIn state to another
    toggleAllPreviewsOpenIn({ commit }, { timelineId, fromOpenIn }) {
        commit('TOGGLE_ALL_PREVIEWS_OPEN_IN', { timelineId, fromOpenIn });
    }
};

export const mutations = {
    ADD_PREVIEW(state, preview) {
        state.previews.push(preview);
    },

    REMOVE_PREVIEW(state, id) {
        const preview = state.previews.find(p => p.id === id);
        // Clean up URLs before removing
        if (preview?.loadedData?.url) {
            URL.revokeObjectURL(preview.loadedData.url);
        }
        state.previews = state.previews.filter(p => p.id !== id);
    },

    SET_PREVIEW_DATA(state, { id, loadedData }) {
        const preview = state.previews.find(p => p.id === id);
        if (preview) {
            preview.loadedData = loadedData;
        }
    },

    TOGGLE_OPEN_IN(state, id) {
        const preview = state.previews.find(p => p.id === id);
        if (preview) {
            preview.openIn = preview.openIn === 'sidepanel' ? 'inline' : 'sidepanel';
        }
    },

    SET_OPEN_IN(state, { id, openIn }) {
        const preview = state.previews.find(p => p.id === id);
        if (preview) {
            preview.openIn = openIn;
        }
    },

    REMOVE_TIMELINE_PREVIEWS(state, timelineId) {
        state.previews = state.previews.filter(p => p.timelineId !== timelineId);
    },

    TOGGLE_ALL_PREVIEWS_OPEN_IN(state, { timelineId, fromOpenIn }) {
        const toOpenIn = fromOpenIn === 'sidepanel' ? 'inline' : 'sidepanel';
        state.previews.forEach(preview => {
            if (preview.timelineId === timelineId && preview.openIn === fromOpenIn) {
                preview.openIn = toOpenIn;
            }
        });
    }
};
